import { hydrate } from '~/utils/object'
import type { Nullable } from '~/utils/nullable'
import { JStep } from '~/models/documents/jStep'

export type TDocumentGroupBy =
  | 'status'
  | 'workplaces'
  | 'products'
  | 'categories'
  | 'templates'
  | 'operations'
  | 'no_grouping'

export enum Days {
  sunday = 0,
  monday = 1,
  tuesday = 2,
  wednesday = 3,
  thursday = 4,
  friday = 5,
  saturday = 6,
  ALL = 7,
}

export enum DocumentListType {
  DOCUMENTS = 'documents',
  TEMPLATES = 'templates',
  SIMULATION = 'simulation',
}

export enum DocumentStatus {
  new = 1,
  in_progress,
  finished,
  draft,
  published,
  archived,
  deleted,
}

export enum DocumentState {
  new = 'new',
  upgrade = 'upgrade',
}

export enum DocumentType {
  readonly = 'readonly',
  read_write = 'read_write',
  automatic = 'automatic',
}

export enum DisplayColumnType {
  all_columns = 'all_columns',
  editable_columns = 'editable_columns',
}

export enum DisplayLineType {
  all_lines = 'all_lines',
  editable_lines = 'editable_lines',
}

export enum ApplicationFieldType {
  product = 'product',
  workplace = 'workplace',
  operation = 'operation',
}

export enum ApplicationFieldAllType {
  ALL_PRODUCTS = 'ALL_PRODUCTS',
  ALL_WORKPLACES = 'ALL_WORKPLACES',
  ALL_OPERATIONS = 'ALL_OPERATIONS',
}

export enum ApplicationFieldSearchType {
  SEARCH_PRODUCTS = 'SEARCH_PRODUCTS',
  SEARCH_WORKPLACES = 'SEARCH_WORKPLACES',
  SEARCH_OPERATIONS = 'SEARCH_OPERATIONS',
}

export enum DocumentTrigger {
  // team = 'team',
  // day = 'day',
  production_order = 'production_order',
}

export enum DocumentFormat {
  Unique = 'Unique',
  Quantity = 'Quantity',
  Fixed = 'Fixed',
  Unlimited = 'Unlimited',
}

export class ApplicationField {
  id = ''
  type: ApplicationFieldType = ApplicationFieldType.product
  label?: string
}

export enum FrequencyType {
  minute = 'minute',
  hour = 'hour',
  day = 'day',
}

export enum RecurrenceType {
  no_schedule = 'no_schedule',
  day = 'day',
  week = 'week',
  month = 'month',
  year = 'year',
}

export class Schedule {
  days = [] as Days[]
  recurrenceType?: RecurrenceType = RecurrenceType.day
  recurrence?: Nullable<number> = 1
  recurrence_starting_date: string | null | number | Date = null
  recurrence_hours: { from: string; to: string }[] = []
  recurrence_hours_enabled = false

  constructor(object: Schedule | object = {}) {
    for (const key in object) this[key] = object[key]
  }
}

export class Frequency {
  enabled = false
  type: FrequencyType | null = null
  every: number | null = null
  startingAt = ''
  enableNotification = false
  startWithReportLaunch = true

  constructor(object: Frequency | object = {}) {
    for (const key in object) this[key] = object[key]
  }
}

export enum FrequencyType {
  minute = 'minute',
  hour = 'hour',
  day = 'day',
}

export class RolesAcces {
  label = ''
  value = 0

  constructor(object: Frequency | object = {}) {
    for (const key in object) this[key] = object[key]
  }
}

export interface DuplicateDocumentOptions {
  siteId: string
  translateLang: string
  newVersion?: boolean
}

export class DocumentCategory {
  blocker = false
  justify_ko = false
  mandatory_finish = false
  order = 0
  roles_acces = [] as RolesAcces[]
  client_id = ''
  site_id = ''
  value = ''

  // Audit
  creation_date: any = ''
  update_date: any = ''
  deleted_at = ''

  type: string | null = null

  constructor(object: Frequency | object = {}) {
    for (const key in object) this[key] = object[key]
  }
}

export class JDocument {
  // permet d'ajouter à la volé uniquement en dehors de l'object, ex: document.AttrInvente = valIncroyable
  [x: string]: any

  // General
  id = ''
  name = ''
  description = ''
  displayType: DisplayColumnType = DisplayColumnType.all_columns
  displayLineType: DisplayLineType = DisplayLineType.all_lines
  status: DocumentStatus = DocumentStatus.draft
  type: DocumentType = DocumentType.read_write
  client_id = ''
  site_id = ''
  is_multiple = false
  trigger: DocumentTrigger = DocumentTrigger.production_order
  format = {
    type: DocumentFormat.Unique,
    value: null,
    formula: null,
  }

  attachment_id = ''
  attachment_ids = []

  // Content
  steps: Array<string | JStep> = [] // convert
  applicationfields: Array<ApplicationField> = []
  templates: Array<string> = []
  workflow_id = ''

  // Parameters
  category = ''
  order = 1
  scheduling = ''
  schedule: Schedule = {
    days: [] as Days[],
    recurrenceType: RecurrenceType.day,
    recurrence: 1,
    recurrence_starting_date: '' as any,
    recurrence_hours: [] as { from: string; to: string }[],
    recurrence_hours_enabled: false,
  } as Schedule

  is_monocolumn = false

  frequency: Frequency = {
    enabled: false,
    type: null,
    every: null,
    startingAt: '',
    enableNotification: false,
    startWithReportLaunch: true,
  }

  starting_date: any = ''
  expiration_date: any = ''
  grid_header: any = ''

  // NIR
  device_id = ''
  device_name = ''
  // Audit
  creation_date: any = ''
  update_date: any = ''
  created_by = ''
  updated_by = ''

  // Versioning
  version = '1.0'
  version_reason = ''
  root_version_document_id = ''
  next_version_document_id?: string = ''

  constructor(object: typeof JDocument | {}) {
    hydrate(this, object)
  }
}
